import React from 'react';
import './WalletSelectionModal.css';

const WalletSelectionModal = ({ isOpen, onClose, onSelectWallet, availableWallets, privyIdentityLabel, onSwitchAccount }) => {
    if (!isOpen) return null;
    
    // Show loading state if wallets are still being discovered
    const isLoading = availableWallets === null;

    // NOTE: Privy is the single entry point — its own modal offers email,
    // Google and external wallets (MetaMask, Coinbase, Rainbow, WalletConnect),
    // so the per-wallet options below are no longer needed here.
    const wallets = [
        {
            id: 'privy',
            name: 'Privy',
            icon: '/privy.png',
            description: 'Continue with email, Google, or your own wallet',
            installUrl: 'https://docs.privy.io'
        },
        // {
        //     id: 'metamask',
        //     name: 'MetaMask',
        //     icon: '/metamask.png',
        //     description: 'Connect using MetaMask extension',
        //     installUrl: 'https://metamask.io/download/'
        // },
        // {
        //     id: 'coinbase',
        //     name: 'Coinbase Wallet',
        //     icon: '/coinbase.png',
        //     description: 'Connect using Coinbase Wallet browser extension',
        //     installUrl: 'https://www.coinbase.com/wallet'
        // },
        // {
        //     id: 'walletconnect-trust',
        //     name: 'Trust Wallet',
        //     icon: '/trust.png',
        //     description: 'Connect via desktop extension or mobile via WalletConnect',
        //     installUrl: 'https://trustwallet.com/'
        // },
        // {
        //     id: 'walletconnect-okx',
        //     name: 'OKX Wallet',
        //     icon: '/okx.png',
        //     description: 'Connect via desktop extension or mobile via WalletConnect',
        //     installUrl: 'https://www.okx.com/web3'
        // },
        // {
        //     id: 'rainbow',
        //     name: 'Rainbow Wallet',
        //     icon: '/rainbow.png',
        //     description: 'Connect using Rainbow Wallet browser extension',
        //     installUrl: 'https://rainbow.me/'
        // }
    ];

    const handleWalletClick = (e, walletId) => {
        // CRITICAL: Stop all event propagation immediately
        if (e && e.stopPropagation) e.stopPropagation();
        if (e && e.preventDefault) e.preventDefault();
        if (e && e.stopImmediatePropagation) e.stopImmediatePropagation();

        // Normalize walletId (trim whitespace, ensure it's a string)
        const normalizedWalletId = String(walletId).trim();

        // Verify the walletId is valid
        const wallet = wallets.find(w => w.id === normalizedWalletId);

        if (!wallet) {
            console.error('[WalletSelectionModal] Wallet not found in wallets array:', normalizedWalletId, 'Available wallets:', wallets.map(w => w.id));
            return;
        }

        // For Privy, always allow clicking - it's SDK-driven, not an extension,
        // so it doesn't depend on availableWallets (extension-detection result)
        // the way MetaMask/Coinbase/etc. below do. Checking this before the
        // isLoading gate matters: that detection can take a while (or, on a
        // slow connection, longer than a user is willing to wait), and there
        // is no reason to make Privy wait on it too.
        if (normalizedWalletId === 'privy') {
            // This used to close the modal, then call onSelectWallet 100ms
            // later via setTimeout "to ensure modal closes before calling
            // onSelectWallet" — same failure mode diagnosed earlier in
            // TokenSelector.js: a timer scheduled right as the component
            // that scheduled it unmounts (onClose() flips isOpen to false
            // synchronously) is not reliably guaranteed to fire. Calling
            // onClose() first still closes the modal before onSelectWallet
            // runs, since this is all synchronous — no timer needed.
            onClose();
            onSelectWallet('privy');
            return;
        }

        if (isLoading) {
            return;
        }


        // For WalletConnect wallets, always allow clicking - let the connection handler deal with configuration
        if (normalizedWalletId.startsWith('walletconnect-')) {
            onClose();
            onSelectWallet(normalizedWalletId);
            return;
        }

        // For other wallets (like MetaMask), check availability
        const isAvailable = availableWallets && availableWallets[normalizedWalletId];

        if (isAvailable) {
            onClose();
            onSelectWallet(normalizedWalletId);
        } else {
            // Show installation message
            const message = `${wallet.name} is not installed. Would you like to visit the ${wallet.name} website to install it?`;
            if (window.confirm(message)) {
                const opened = window.open(wallet.installUrl, '_blank', 'noopener,noreferrer');
                if (opened) opened.opener = null;
            }
        }
    };

    return (
        <div className="wallet-selection-overlay" onClick={onClose}>
            <div className="wallet-selection-modal" onClick={(e) => e.stopPropagation()}>
                <div className="wallet-selection-header">
                    <h2>Connect Wallet</h2>
                    <button className="close-button" onClick={onClose}>Close</button>
                </div>
                <div className="wallet-selection-content">
                    <p className="wallet-selection-description">
                        Choose a wallet to connect to your account
                    </p>
                    <div className="wallet-options">
                        {wallets.map((wallet) => {
                            // For Privy, always show as available - it's SDK-driven, not an extension
                            const isPrivy = wallet.id === 'privy';
                            // For WalletConnect wallets, always show as available (connection will handle errors)
                            let isAvailable;
                            if (isPrivy) {
                                isAvailable = true; // Privy is SDK-driven, not an extension
                            } else if (wallet.id.startsWith('walletconnect-')) {
                                isAvailable = isLoading ? null : true; // Always available via WalletConnect
                            } else {
                                isAvailable = isLoading ? null : (availableWallets && availableWallets[wallet.id]);
                            }
                            const showLoading = isLoading && !isPrivy;
                            const availabilityClass =
                                isAvailable === true
                                    ? 'available'
                                    : isAvailable === false
                                        ? 'not-installed'
                                        : 'loading';
                            const computedCursor = showLoading ? 'wait' : 'pointer';
                            const tabIndex = showLoading ? -1 : 0;
                            const arrowClass = `wallet-arrow ${isAvailable === true ? 'wallet-arrow-visible' : ''}`;
                            
                            return (
                                <div
                                    key={wallet.id}
                                    className={`wallet-option ${availabilityClass}`}
                                    onClick={(e) => {
                                        handleWalletClick(e, wallet.id);
                                    }}
                                    onMouseDown={(e) => {
                                        // Prevent any default behavior on mouse down
                                        if (!showLoading) {
                                            e.stopPropagation();
                                        }
                                    }}
                                    style={{ cursor: computedCursor, opacity: showLoading ? 0.65 : 1 }}
                                    role="button"
                                    tabIndex={tabIndex}
                                    aria-label={`Connect with ${wallet.name}`}
                                >
                                    <div 
                                        className="wallet-icon"
                                    >
                                        <img src={wallet.icon} alt={wallet.name} />
                                    </div>
                                    <div className="wallet-info">
                                        <div className="wallet-name-row">
                                            <h3>{wallet.name}</h3>
                                            {showLoading ? (
                                                <span className="loading-badge">Checking...</span>
                                            ) : null}
                                        </div>
                                        <p className="wallet-description">
                                            {isPrivy && privyIdentityLabel
                                                ? `Continue as ${privyIdentityLabel}`
                                                : wallet.description}
                                        </p>
                                    </div>
                                    <div className={arrowClass}>
                                        →
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* A Privy session outlives a disconnect, so connecting again
                        would otherwise reuse the previous account with no way out. */}
                    {onSwitchAccount ? (
                        <button
                            type="button"
                            className="wallet-switch-account-link"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                                onSwitchAccount();
                            }}
                        >
                            Use a different account
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default WalletSelectionModal;
